import { autoinject, BindingEngine, computedFrom, transient } from "aurelia-framework";
import { MenuItem } from "../../../components/menu/menu-item";
import { StepModel, ClaimsRequireTypeEnum } from "../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { Router } from "aurelia-router";
import { BusinessUnitOverview } from "./tabs/business-unit-overview";
import { BusinessUnitRuleConfig } from "./tabs/business-unit-rule-config";
import { BusinessUnitEmailAlertSetup } from "./tabs/business-unit-email-alert-setup/business-unit-email-alert-setup";
import {
    BusinessUnitModel,
    SaveBusinessUnitRequest
} from "../../../../models/customers/customer.models";
import { Reflection } from "../../../../utilities/reflection";
import { ActiveMenuItemsService } from "../../../../services/basics/active-menu-items.service";
import { Guid } from "../../../../utilities/guid";
import { UnitOfWorkObserver } from "../../../../utilities/unit-of-work-observer";
import { CustomerService } from "../../../../services/modules/customer-client.service";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { I18N } from "aurelia-i18n";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { ValidationsClientService } from "../../../../services/validations/validations-client.service";
import { Claims } from "../../../../constants/authentication/claims";
import { RightShipDialogServiceConstants } from "../../../../constants/basics/rightship-dialog.constants";
import * as Enumerable from "linq";
import { BusinessUnitRuleHistoryTrail } from "./tabs/business-unit-rule-history-trail";
import { RiskCategoryLevelEnum } from "../../../../models/rules/rules.models";
import { RuleRiskConfigs } from "../customers/steps/rules/rule-risk-configs";
import { BusinessUnitPortsAndTerminals } from "./tabs/business-unit-ports-and-terminals";
import { EmailRecipientTypeEnum, VettingActionEnum } from "../../../../models/vetting/vetting.models";
import { BusinessUnitRuleCollections } from "../customers/steps/rules/rule-collections";
import { BusinessUnitRightstoreBudget } from "./tabs/rightstore-budget/business-unit-rightstore-budget";
import { RightshipConfirmationDialogModel } from "../../../dialogs/rightship-confirmation-dialog/rightship-confirmation-dialog";
import { SignalFunctions } from "../../../../services/basics/signal-functions";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";

@autoinject()
@transient()
export class BusinessUnit {

    private _tabs: StepModel[] = [];
    private _canAccessBusinessUnitRuleParameters: boolean;
    private _canEditBusinessUnitRuleParameters: boolean;
    private _canViewBUEmailAlerts: boolean;
    private _canAccessPortsAndTerminalsSetup: boolean;
    private _canAccessBudgetSetup: boolean;

    protected model: BusinessUnitModel;
    protected uow: UnitOfWorkObserver;

    public canEditBusinessUnitEmailAlerts: boolean;
    public currentTab: StepModel = null;
    public currentTabKey: string = "business-unit-tabs";
    public viewModelInCompose: any = null;
    public isExporting: boolean = false;
    public modelInCompose: BusinessUnitModel | { businessUnit?: BusinessUnitModel, isReadOnly?: boolean, id?: number, level?: RiskCategoryLevelEnum, isRisk?: boolean };
    public parameterIdsUsed: number[];
    public menuItems: MenuItem[] = [];

    constructor(private router: Router, private businessUnitOverview: BusinessUnitOverview, private businessUnitRuleConfig: BusinessUnitRuleConfig,
        private businessUnitVetAlertSetup: BusinessUnitEmailAlertSetup, private businessUnitPortsAndTerminals: BusinessUnitPortsAndTerminals,
        private customerService: CustomerService, private bindingEngine: BindingEngine, private userClaimsService: UserClaimsService,
        private dialogClientService: DialogClientService, private i18N: I18N, private businessUnitRuleCollections: BusinessUnitRuleCollections,
        private notificationClientService: NotificationClientService, private validationsClientService: ValidationsClientService,
        private activeMenuItemsService: ActiveMenuItemsService, private ruleRiskConfigs: RuleRiskConfigs,
        private businessUnitRuleHistoryTrail: BusinessUnitRuleHistoryTrail, private businessUnitRightstoreBudget: BusinessUnitRightstoreBudget,
        private signalService: SignalClientService) {

        this._canAccessBusinessUnitRuleParameters = this.userClaimsService.hasPermission(Claims.CanAccessBusinessUnitRuleParameters);
        this._canEditBusinessUnitRuleParameters = this.userClaimsService.hasPermission(Claims.CanEditBusinessUnitRuleParameters);
        this._canViewBUEmailAlerts = this.userClaimsService.hasPermission(Claims.CanViewBUEmailAlerts);
        this._canAccessPortsAndTerminalsSetup = this.userClaimsService.hasPermission(Claims.CanAccessPortsAndTerminalsSetup);
        this._canAccessBudgetSetup = this.userClaimsService.hasPermission(Claims.GetAllBudgets);
        this.canEditBusinessUnitEmailAlerts = this.userClaimsService.hasPermission(Claims.CanEditBUEmailAlerts);

        const canDelete = this.userClaimsService.hasPermission(Claims.CanRemoveBusinessUnitSettings);

        this.menuItems = [
            {
                click: () => {
                    this.isExporting = true;
                    this.customerService.downloadRulesExcel(this.model.customerId, this.model.businessUnitId, this.model.name).then(() => {
                        this.isExporting = false;
                    },  () => {
                        this.isExporting = false;
                    })
                },
                isVisible: () => this.model.businessUnitId != null && this.userClaimsService.hasPermission(Claims.CanAccessCustomerRuleParameters),
                item: {
                    exporticon: true,
                    text: 'Export rules'
                },
                classList: '',
            },
            {
                click: this.deleteBusinessUnit.bind(this),
                isVisible: canDelete,
                item: {
                    deleteicon: true,
                    text: 'Delete business unit'
                },
                classList: '',
            }
        ].filter(x => x.isVisible);
    }

    @computedFrom(Reflection.getPropertyPath<BusinessUnit>(bu => bu.router.currentInstruction))
    public get previousInstructionTitle(): string {
        const title = this.router.history.getState("previousRouteName");

        if (title && title.trim() != "" && title != this.i18N.tr('back-text')) {
            return title;
        }

        const prevInstruction = this.router.currentInstruction.previousInstruction;

        return prevInstruction
            ? prevInstruction.config.title
            : this.i18N.tr('back-text');
    }

    @computedFrom('currentTab')
    get hideSaveButton() {
        return this.currentTab?.name == "email-alert-setup" && !this.canEditBusinessUnitEmailAlerts; 
    }

    saveRisk() {
        return this.ruleRiskConfigs.save();
    }

    @computedFrom(Reflection.getPropertyPath<BusinessUnit>(c => c._tabs))
    get tabs(): StepModel[] {
        return this._tabs;
    }

    private updateTabs() {
        this.modelInCompose = this.model;
        this._tabs = [
            {
                lazyLoad: true,
                show: () => true,
                title: "Overview",
                step: 1,
                onClick: () => {
                    this.viewModelInCompose = this.businessUnitOverview;
                    this.modelInCompose = this.model;
                }
            },
            {
                lazyLoad: true,
                show: () => this._canAccessBusinessUnitRuleParameters || this._canEditBusinessUnitRuleParameters,
                title: "Rule Parameters",
                step: 2,
                onClick: () => {
                    this.viewModelInCompose = this.businessUnitRuleConfig;
                    this.modelInCompose = { businessUnit: this.model, isReadOnly: !this._canEditBusinessUnitRuleParameters };
                }
            },
            {
                lazyLoad: true,
                show: () => this._canAccessBusinessUnitRuleParameters || this._canEditBusinessUnitRuleParameters,
                title: "History Trail",
                step: 4,
                onClick: () => {
                    this.viewModelInCompose = this.businessUnitRuleHistoryTrail;
                    this.modelInCompose = { businessUnitId: this.model.businessUnitId, businessUnit: this.model, isReadOnly: true };
                }
            },
            {
                name: "email-alert-setup",
                lazyLoad: true,
                show: () => this._canViewBUEmailAlerts,
                title: "Email Alert Setup",
                step: 5,
                onClick: () => {
                    this.modelInCompose = this.model;
                    this.viewModelInCompose = this.businessUnitVetAlertSetup
                }
            },
            {
                lazyLoad: true,
                title: "Rule Collections",
                step: 6,
                show: this.userClaimsService.hasPermissions([Claims.CanAccessRuleCollectionsAtBusinessUnit], ClaimsRequireTypeEnum.RequireAny)
                    && this.model.businessUnitId > 0,
                onClick: () => {
                    this.viewModelInCompose = this.businessUnitRuleCollections;
                    this.modelInCompose = this.model;
                },
            },
            {
                lazyLoad: true,
                show: () => this._canAccessBudgetSetup,
                title: "Rightstore budget",
                step: 7,
                onClick: () => {
                    this.viewModelInCompose = this.businessUnitRightstoreBudget;
                    this.modelInCompose = this.model;
                }
            },
            {
                lazyLoad: true,
                show: () => this._canAccessPortsAndTerminalsSetup,
                title: "Ports & Terminals Setup",
                step: 8,
                onClick: () => {
                    this.viewModelInCompose = this.businessUnitPortsAndTerminals;
                    this.modelInCompose = this.model;
                }
            }
        ];
    }

    async activate(model: { customerId: number, businessUnitId: number, isHosted?: boolean }) {
        this.activeMenuItemsService.setActiveFromMenuItem('Customers');

        return new Promise<void>((resolve, reject) => {
            if (Number(model.businessUnitId)) {
                this.customerService.getBusinessUnit(model.businessUnitId).then(x => {
                    this.model = x.businessUnits[0];
                    this.parameterIdsUsed = x.parameterIdsUsed;
                    this.model.parameterIdsUsed = x.parameterIdsUsed;
                    for (const buva of this.model.businessUnitVetAlerts) {
                        buva.businessUnitVetAlertRecipients = Enumerable.from(buva.businessUnitVetAlertRecipients).orderBy(y => y.forVetRequester ? -1 : 0).toArray();
                    }
                    if (this.uow) {
                        this.uow.clearChanges();
                        this.uow.dispose();
                    }
                    this.uow = UnitOfWorkObserver.getTrackingInstance(this.bindingEngine, this.model, ["parameterIdsUsed"]);
                    setTimeout(() => {
                        this.updateTabs();
                        resolve();
                    });

                }, reject);
            } else if (model.customerId) {
                const isHosted = typeof model.isHosted === 'string'? Boolean(JSON.parse(model.isHosted)): model.isHosted;
                this.model = {
                    businessUnitId: 0,
                    portsAndTerminalsDepartureDaysFromArrival: null,
                    customerId: 0,
                    isActive: false,
                    contactShipManagersDirectly: !isHosted,
                    businessUnitUsers: [],
                    businessUnitVetAlerts: [],
                    businessUnitVettingLists: [],
                    numberOfDaysBeforeVetExpires: 0,
                    numberOfDaysBeforeFormExpires: 0,
                    numberOfDaysBeforeDueDiligenceExpires: 0,
                    name: null,
                    shouldAddVesselsToVettingList: null,
                    entityType: EntityTypes.Customer,
                    identifier: Guid.generateGuid(),
                    code: null,
                    showVetBanner: null
                };

                this.uow = UnitOfWorkObserver.getTrackingInstance(this.bindingEngine, this.model);
                setTimeout(() => {
                    this.model.isActive = true;
                    this.model.customerId = model.customerId;
                    this.model.portsAndTerminalsDepartureDaysFromArrival = 3;
                    this.model.businessUnitVetAlerts.push({
                        isActive: true,
                        vettingActionId: VettingActionEnum.VetHasAnOutcome,
                        businessUnitVetAlertIncoTerms: [],
                        businessUnitVetAlertOnboardingConfigs: [],
                        businessUnitVetAlertVesselTypes: [],
                        businessUnitVetAlertPorts: [],
                        businessUnitVetAlertRfiTemplates: [],
                        businessUnitVetAlertRecipients: [{
                            forVetRequester: true,
                            email: '',
                            emailRecipientTypeId: EmailRecipientTypeEnum.To,
                            forFbrSubmitter: false,
                            forShipManager: false,
                            forVoyageDataSubmitter: false,
                            forCollaborators: false
                        }],
                        customerId: model.customerId
                    });
                    this.updateTabs();
                    resolve();
                });
            }
        });
    }
    
    attached()
    {
        this.signalService.subscribe(SignalFunctions.customerActiveStateChanged , this.onCustomerActiveStateChanged.bind(this));
    }
    
    detached() {
        this.signalService.unsubscribe(SignalFunctions.customerActiveStateChanged , this.onCustomerActiveStateChanged.bind(this));
    }

    async onCustomerActiveStateChanged(data: {customerId: number, businessUnitIds: number[] }) 
    {
        if(data && data.businessUnitIds.indexOf(this.model.businessUnitId) > -1)
        {
            this.customerService.clearCustomerAndBusinessUnitCache(data.customerId, data.businessUnitIds);
            //reload customer if no changes
            if(!this.areThereUnsavedChanges())
            {
                this.reload();
            }
        }
    }

    reload() {
        setTimeout(() => {
            this.activate({ customerId: this.model.customerId, businessUnitId: this.model.businessUnitId });
            if (this.currentTab && this.currentTab.step == 1)
                this.viewModelInCompose.activate(this.modelInCompose);
        });
    }
    
    protected async showDeactivationWarning() {
        const allChanges = this.uow.getChanges();
        if(Object.keys(allChanges).indexOf(Reflection.getPropertyName<BusinessUnitModel>(x => x.isActive)) > -1 && !this.model.isActive) 
        {
            const model = new RightshipConfirmationDialogModel();
            model.confirm = this.i18N.tr('confirm-text');
            model.cancel = this.i18N.tr('cancel-text');

            model.messages.push('Deactivating this business unit will also deactivate active users. Do you want to continue? ');

            model.title = 'Deactivate Business Unit';

            model.onConfirm = () => this.saveInternal();
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RightshipConfirmationDialog, model, true, false, false);
        }
        else 
        {
            await this.saveInternal();
        }
    }

    private saveInternal()
    {
        return new Promise<void>((resolve, reject) => {
            const hasPortConfigChanges = this.businessUnitPortsAndTerminals.hasPortConfigChanges();
            const allChanges = this.uow.getChanges();
            this.customerService.saveBusinessUnit(<SaveBusinessUnitRequest>{
                id: this.model.businessUnitId,
                changes: allChanges
            }).then(x => {
                if (hasPortConfigChanges) {
                    this.businessUnitPortsAndTerminals.savePortConfig().then(() => {
                        this.notificationClientService.show(this.i18N.tr('changes-saved-successfully-text'), "", NotificationType.Success);

                        this.model.businessUnitId = x.generatedId;
                        this.customerService.clearBusinessUnitPromiseCache(this.model.businessUnitId);
                        this.reload();
                        resolve();
                    }).catch(e => {
                        console.error(e);
                        this.notificationClientService.show(this.i18N.tr('failed-save-customer'), "Failed saving port configuration", NotificationType.Error);
                        reject();
                    });
                }
                else {
                    this.notificationClientService.show(this.i18N.tr('changes-saved-successfully-text'), "", NotificationType.Success);

                    this.model.businessUnitId = x.generatedId;
                    this.customerService.clearBusinessUnitPromiseCache(this.model.businessUnitId);
                    this.reload();
                    resolve();
                }

            }).catch(x => {
                if (x.validationResult && x.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(x.validationResult);
                } else {
                    this.notificationClientService.show(this.i18N.tr('failed-save-customer'), "", NotificationType.Error);
                }
                reject();
            });
        });
    }

    async saveDomains() {
        const isValid = await this.businessUnitOverview.isValid();
        if (isValid && this.model) {
            const businessUnitDomainUpdateRequest = {
                businessUnitId: this.model.businessUnitId ? this.model.businessUnitId : null,
                allowedDomains: this.model.allowedDomains ? this.model.allowedDomains.map(x => x.name): []
            };
            await this.customerService.updateBusinessUnitAllowedDomains(businessUnitDomainUpdateRequest);
            this.notificationClientService.show(this.i18N.tr('changes-saved-successfully-text'), "", NotificationType.Success);
            await this.businessUnitOverview.loadBusinessDomains();
        }
    }

    save() {
        return new Promise<void>((resolve, reject) => {
            setTimeout(async () => {
                if(this._canAccessPortsAndTerminalsSetup)
                {
                    const valid = await this.businessUnitPortsAndTerminals.isValid();
                    if(!valid)
                    {
                        reject();
                        return;
                    }
                }

                const hasUowChanges = this.uow.hasChanges(true);
                const hasPortConfigChanges = this.businessUnitPortsAndTerminals.hasPortConfigChanges();

                if (!hasUowChanges && !hasPortConfigChanges && !this.businessUnitOverview.hasChanges()) {
                    this.notificationClientService.show(this.i18N.tr('no-changes-performed-text'), this.i18N.tr('there-were-no-changes-to-save-text'), NotificationType.Info);
                    resolve();
                    return;
                }

                //Validate input fields
                if (this.model.portsAndTerminalsDepartureDaysFromArrival <= 0) {
                    this.notificationClientService.show('Invalid Departure days from arrival', 'Departure days from arrival must be at least 1 day', NotificationType.Error);
                    reject();
                    return;
                }

                if (hasUowChanges) {
                    this.showDeactivationWarning().then(() => {
                        this.saveDomains();
                        resolve();
                    }).catch(() => {
                        reject();
                    });
                }
                else {
                    if (hasPortConfigChanges) {
                        this.businessUnitPortsAndTerminals.savePortConfig().then(() => {
                            this.notificationClientService.show(this.i18N.tr('changes-saved-successfully-text'), "", NotificationType.Success);
                            this.reload();
                            resolve();
                        }).catch(e => {
                            console.error(e);
                            this.notificationClientService.show(this.i18N.tr('failed-save-customer'), "Failed saving port configuration", NotificationType.Error);
                            reject();
                        });
                    }

                    this.saveDomains().then(() => {
                        resolve();
                    }).catch(e => {
                        console.error(e);
                        this.notificationClientService.show(this.i18N.tr('failed-save-customer'), "Failed saving bussiness domains", NotificationType.Error);
                        reject();
                    });
                }  
            });
        });
    }

    public navigateBack() {
        this.router.navigateBack();
    }

    deleteBusinessUnit() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.DeleteModal, this, true, false, false, 'right');
    }

    delete = () => {
        this.customerService.deleteBusinessUnit(this.model.businessUnitId).then(() => {
            this.router.navigateBack();
        });
    };

    areThereUnsavedChanges() 
    {
        const hasUowChanges = this.uow.hasChanges(true);
        const hasPortConfigChanges = this.businessUnitPortsAndTerminals.hasPortConfigChanges();
        return (hasUowChanges || hasPortConfigChanges || this.businessUnitOverview.hasChanges());
    }

    async canDeactivate() 
    {
        if (this.areThereUnsavedChanges()) {
            const result = await new Promise<boolean>((resolve) => {
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.LeaveWithoutSavingDialog, {
                    save: () => {
                        this.save().then(() => {
                            resolve(true);
                        }).catch(() => {
                            resolve(false);
                        });
                    },
                    cancel: () => {
                        resolve(true);
                    },
                    close: () => {
                        resolve(false);
                    }
                }, true);

            });
            return result;
        }
    }
}
